<template>
  <LoadingBar ref="loadingBar" />
  <header class="fixed inset-x-0 top-0 z-[9999] border-b border-slate-200/80 bg-slate-50/95 backdrop-blur-md dark:border-slate-800/80 dark:bg-slate-950/95">
    <nav class="container mx-auto flex min-h-[64px] items-center justify-between px-3 lg:px-6">
      <div class="flex min-w-0 items-center">
        <router-link :to="langPath" class="flex shrink-0 items-center">
          <img src="@/assets/images/logo.png" class="h-10 w-10" alt="Logo" />
          <img src="@/assets/images/text-logo.png" class="ms-2 hidden w-40 md:block" alt="Binesheno" />
        </router-link>
        <ul v-if="headerMenu.length" class="ms-5 hidden items-center rounded-2xl border border-slate-200/70 bg-white/40 p-1 dark:border-slate-800 dark:bg-slate-900/40 xl:flex">
          <DynamicMenuItem v-for="item in headerMenu" :key="item.id || item.title" :item="item" :lang-path="langPath" />
        </ul>
      </div>
      <div class="flex items-center gap-2">
        <div class="relative">
          <button type="button" class="rounded-xl px-3 py-2 text-sm text-slate-700 hover:bg-slate-200 dark:text-slate-200 dark:hover:bg-slate-800" @click="langMenuOpen = !langMenuOpen"> <span>{{ $t('select_language') }}</span></button>
          <div v-show="langMenuOpen" class="absolute end-0 top-full mt-2 w-44 overflow-hidden rounded-xl border bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900">
            <button v-for="item in language" :key="item.id" type="button" class="block w-full px-4 py-2.5 text-start text-sm hover:bg-slate-100 dark:hover:bg-slate-800" @click="setLanguage(item)">{{ item.title }}</button>
          </div>
        </div>
        <label class="relative inline-flex cursor-pointer items-center" dir="ltr">
          <input ref="darkModeCheckbox" type="checkbox" class="peer sr-only" @change="toggleDarkMode" />
          <span class="relative h-5 w-9 rounded-full bg-slate-300 after:absolute after:start-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:bg-primary peer-checked:after:translate-x-full dark:bg-slate-700"></span>
        </label>
        <button type="button" class="rounded-xl p-2 text-slate-700 hover:bg-slate-200 xl:hidden dark:text-slate-200 dark:hover:bg-slate-800" :aria-expanded="mobileMenuOpen" :aria-label="$t(mobileMenuOpen ? 'close_menu' : 'open_menu')" @click="mobileMenuOpen = !mobileMenuOpen">
          <svg v-if="!mobileMenuOpen" class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
          <svg v-else class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m6 6 12 12M18 6 6 18" /></svg>
        </button>
      </div>
    </nav>
    <div v-show="mobileMenuOpen" class="border-t bg-white px-4 py-3 shadow-xl xl:hidden dark:border-slate-800 dark:bg-slate-950">
      <div v-if="menuLoading" class="py-4 text-center text-sm text-slate-500">{{ $t('loading_menu') }}</div>
      <ul v-else class="mx-auto max-h-[calc(100vh-90px)] max-w-lg overflow-y-auto">
        <DynamicMenuItem v-for="item in headerMenu" :key="item.id || item.title" :item="item" :lang-path="langPath" mobile @navigate="mobileMenuOpen = false" />
      </ul>
    </div>
  </header>

  <main class="mt-[64px] min-h-[calc(100vh-64px)] bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-white">
    <router-view v-slot="{ Component, route }"><component :is="Component" :key="currentLang + '-' + route.fullPath" /></router-view>
  </main>

  <footer class="border-t border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950">
    <div class="container mx-auto px-4 py-8">
      <div class="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
        <router-link :to="langPath" class="flex items-center gap-3"><img src="@/assets/images/logo.png" class="h-9 w-9" alt="Binesheno" /><span class="rounded-lg bg-primary px-3 py-1.5 text-sm text-white">{{ $t('footer_institute') }}</span></router-link>
        <ul v-if="footerMenu.length" class="flex flex-wrap items-center gap-2"><DynamicMenuItem v-for="item in footerMenu" :key="item.id || item.title" :item="item" :lang-path="langPath" /></ul>
      </div>
      <hr class="my-6 border-slate-200 dark:border-slate-800" />
      <div class="text-center text-sm text-slate-500">© 1404-1405 {{ $t('footer_rights') }}</div>
    </div>
  </footer>
  <button v-if="isVisible" type="button" class="fixed bottom-5 end-4 z-[9998] rounded-xl bg-primary px-3 py-2 text-xs text-white shadow-lg" @click="scrollToTop">{{ $t('back_to_top') }}</button>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";
import LoadingBar from "@/components/LoadingBar.vue";
import DynamicMenuItem from "@/components/DynamicMenuItem.vue";
const fallbackLanguageIds = { fa: 1, ar: 2, en: 3 };

export default {
  name: "TheContainer",
  components: { LoadingBar, DynamicMenuItem },
  data: () => ({ isVisible: false, langMenuOpen: false, mobileMenuOpen: false, menuLoading: false, headerMenu: [], footerMenu: [], menuRequestId: 0 }),
  computed: {
    ...mapGetters({ language: "baseX/language" }),
    currentLang() { return this.$route.path.split("/")[1] || "fa"; },
    langPath() { return `/${this.currentLang}`; },
    currentLanguageId() { return this.language.find(x => x.codeKey === this.currentLang)?.id || fallbackLanguageIds[this.currentLang] || 1; },
  },
  watch: {
    "$route.fullPath"() { this.mobileMenuOpen = false; this.langMenuOpen = false; this.$refs.loadingBar?.startLoading(); setTimeout(() => this.$refs.loadingBar?.stopLoading(), 500); },
    currentLanguageId: { immediate: true, handler(id) { if (id) this.loadMenus(id); } },
  },
  methods: {
    async loadMenus(languageId) {
      const requestId = ++this.menuRequestId; this.menuLoading = true;
      try {
        localStorage.setItem("languageId", languageId);
        const response = await axios.get("client/Menu/LoadMenuPosition", { params: { languageId } });
        const positions = (response.data?.data || []).filter(x => x.isActive !== false);
        const header = positions.find(x => /header|main|بالا|اصلی/i.test(x.title || "")) || positions[0];
        const footer = positions.find(x => /footer|پایین|فوتر/i.test(x.title || "")) || positions.find(x => x.id !== header?.id);
        const getTree = position => position ? axios.get("client/Menu/LoadTree", { params: { menuPositionId: position.id } }) : Promise.resolve({ data: { data: [] } });
        const [headerResult, footerResult] = await Promise.all([getTree(header), getTree(footer)]);
        if (requestId !== this.menuRequestId) return;
        this.headerMenu = headerResult.data?.data || []; this.footerMenu = footerResult.data?.data || [];
      } catch (error) { if (requestId === this.menuRequestId) { this.headerMenu = []; this.footerMenu = []; } console.error("Loading menu failed", error); }
      finally { if (requestId === this.menuRequestId) this.menuLoading = false; }
    },
    setLanguage(item) { if (!item.codeKey) return; this.$i18n.locale = item.codeKey; localStorage.setItem("languageCode", item.codeKey); localStorage.setItem("languageId", item.id); const rest = this.$route.path.replace(/^\/[^/]+/, ""); this.$router.replace(`/${item.codeKey}${rest}`).catch(() => {}); this.langMenuOpen = false; },
    toggleDarkMode(e) { document.documentElement.classList.toggle("dark", e.target.checked); localStorage.setItem("darkMode", e.target.checked ? "enabled" : "disabled"); },
    handleScroll() { this.isVisible = window.scrollY > 300; },
    scrollToTop() { window.scrollTo({ top: 0, behavior: "smooth" }); },
  },
  async mounted() { window.addEventListener("scroll", this.handleScroll); await this.$store.dispatch("baseX/getLanguage"); const dark = localStorage.getItem("darkMode") === "enabled"; document.documentElement.classList.toggle("dark", dark); if (this.$refs.darkModeCheckbox) this.$refs.darkModeCheckbox.checked = dark; },
  beforeUnmount() { window.removeEventListener("scroll", this.handleScroll); },
};
</script>
